import StencilHTMLElement from "./stencil-element";

class SizeGuide extends StencilHTMLElement {
  name = "SizeGuide";
  unit;
  sizes;
  meta;
  sizeSelect;
  unitBtns;
  measureKeys;
  measureEls;
  measuresContainer;

  constructor() {
    super();
    this.unit = "cm";
    this.sizes = [];
    this.meta = {};
  }

  connectedCallback() {
    super.connectedCallback?.();
    console.log(this.logPrefix, "connectedCallback");

    // 1. Parse JSON (sizes + meta)
    const raw = this.getAttribute("json");
    if (!raw) {
      console.error(this.logPrefix, "'json' attribute is missing");
      return;
    }
    let data;
    try {
      data = JSON.parse(raw);
      this.sizes = data.sizes || [];
      this.meta = data.meta || {};
    } catch (e) {
      console.error(this.logPrefix, "Invalid JSON in 'json' attribute:", e);
      return;
    }

    // 2. Cache DOM references
    this.sizeSelect = this.querySelector(".size-select");
    this.unitBtns = Array.from(this.querySelectorAll("[data-unit]"));
    this.measuresContainer = this.querySelector(".measures-container");

    // 3. Build size dropdown
    this.sizes.forEach(s => {
      const opt = document.createElement("option");
      opt.value = s.size;
      opt.textContent = s.size;
      this.sizeSelect.appendChild(opt);
    });

    // 4. Parse measure-icons JSON
    const rawIcons = this.getAttribute("measure-icons") || "{}";
    try {
      this.measureIconUrls = JSON.parse(rawIcons);
    } catch {
      this.measureIconUrls = {};
    }

    // 5. Figure out which measurements to show
    const keysAttr = this.getAttribute("measure-keys") || "";
    this.measureKeys = new Set(
      keysAttr.split(",").map(k => k.trim()).filter(Boolean)
    );

    // 6. Dynamically render measurement columns
    this.measureEls = {};
    const tpl = this.querySelector("template.measure-template");
    const allKeys = [...new Set(this.sizes.flatMap(s => Object.keys(s.measurements)))];
    const keysToShow = this.measureKeys.size
      ? allKeys.filter(key => this.measureKeys.has(key))
      : allKeys;

    keysToShow.forEach(key => {
      const clone = tpl.content.cloneNode(true);

      // image
      const imgEl = clone.querySelector("[data-img]");
      imgEl.src = this.measureIconUrls[key] || "/fallback.png";
      imgEl.alt = this.meta[key]?.label || key;

      // label
      const labelEl = clone.querySelector("[data-label]");
      labelEl.textContent = this.meta[key]?.label || key;

      // value placeholder
      const valEl = clone.querySelector("[data-measure-val]");
      valEl.setAttribute("data-measure-val", key);
      this.measureEls[key] = valEl;

      this.measuresContainer.appendChild(clone);
    });

    // 7. Restore unit from localStorage
    const saved = localStorage.getItem("sizeGuideUnit");
    if (saved === "cm" || saved === "in") this.unit = saved;
    this.unitBtns.forEach(btn =>
      btn.classList.toggle("active", btn.dataset.unit === this.unit)
    );

    // 8. Wire unit buttons
    this.unitBtns.forEach(btn =>
      btn.addEventListener("click", e => {
        this.unitBtns.forEach(b => b.classList.remove("active"));
        e.currentTarget.classList.add("active");
        this.unit = e.currentTarget.dataset.unit;
        localStorage.setItem("sizeGuideUnit", this.unit);
        this.#renderCurrentSize();
      })
    );

    // 9. Wire size dropdown
    this.sizeSelect.addEventListener("change", () => this.#renderCurrentSize());

    // 10. Variant changes
    document.addEventListener("variant:changed", e => this.onVariantChanged(e));

    // 11. Initialize default size
    const defaultSize = this.getAttribute("current-size");
    this.sizeSelect.value = this.sizes.some(s => s.size === defaultSize)
      ? defaultSize
      : (this.sizes[0]?.size || "");

    // Final render
    this.#renderCurrentSize();
  }

  // private helper: render values with view-transitions and min/max support
  #renderCurrentSize() {
    const update = () => {
      const sizeObj = this.sizes.find(s => s.size === this.sizeSelect.value);
      if (!sizeObj) return;

      Object.entries(sizeObj.measurements).forEach(([key, vals]) => {
        const el = this.measureEls[key];
        if (!el) return;

        const m = vals[this.unit];
        let text;
        if (m && typeof m === "object" && "min" in m && "max" in m) {
          text = m.min === m.max ? m.min : `${m.min}–${m.max}`;
        } else {
          text = m;
        }
        text += this.unit === "in" ? `"` : "cm";
        el.textContent = text;
      });
    };

    if (document.startViewTransition) {
      document.startViewTransition(update);
    } else {
      update();
    }
  }

  // handle variant:changed
  onVariantChanged(e) {
    const newSize = e.detail.selectedOption?.dataset?.value;
    if (!newSize) return;
    if (this.sizes.some(s => s.size === newSize)) {
      this.sizeSelect.value = newSize;
      this.#renderCurrentSize();
    }
  }
}

export default SizeGuide;